import { Injectable, inject, DestroyRef } from '@angular/core';
import { ComplexDialogV3Service } from '@gea/digital-ui-lib';
import { DynamicDialogRef } from 'primeng/dynamicdialog';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Organization } from '@shared/models';

export interface CustomerOverviewDialogConfig {
  organizations?: readonly Organization[];
  width?: string;
  closable?: boolean;
  hideButtons?: boolean;
  onClose?: () => void;
}

@Injectable({
  providedIn: 'root',
})
export class CustomerOverviewDialogService {
  private readonly complexDialogService = inject(ComplexDialogV3Service);
  private readonly destroyRef = inject(DestroyRef);

  private dialogRef?: DynamicDialogRef;

  /**
   * Opens the customer overview dialog
   * @param config Dialog configuration options
   * @returns Promise that resolves to the dialog reference
   */
  async openDialog(config: CustomerOverviewDialogConfig = {}): Promise<DynamicDialogRef> {
    // Close any existing dialog first
    this.closeDialog();

    // Dynamically import the component
    const { CustomerOverviewComponent } = await import('@customer-overview/customer-overview.component');

    // Configure dialog options
    const dialogConfig = {
      data: config.organizations || [],
      header: 'CUSTOMER_OVERVIEW.TITLE',
      closable: config.closable ?? true,
      hideButtons: config.hideButtons ?? true,
      variant: 'custom' as const,
      style: {
        width: config.width || '90vw',
        minHeight: '340px !important',
      },
    };

    // Open the dialog
    this.dialogRef = this.complexDialogService.open(dialogConfig, CustomerOverviewComponent).ref;

    // Subscribe to close events
    this.dialogRef.onClose.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.handleDialogClose();
      // Call custom close handler if provided
      if (config.onClose) {
        config.onClose();
      }
    });

    return this.dialogRef;
  }

  /**
   * Closes the customer overview dialog
   */
  closeDialog(): void {
    if (this.dialogRef) {
      this.dialogRef.close();
      this.dialogRef = undefined;
    }
  }

  /**
   * Private method to handle dialog close cleanup
   */
  private handleDialogClose(): void {
    this.dialogRef = undefined;
  }
}
